import { OptionsJson, OptionsUrlencoded } from 'body-parser'
import { ServerMiddleware } from '../server/middleware'
import StaticLocation from '../server/static-location'
import { LogLevel, DeviceType, ServerType } from './config-properties'
import { Profile } from './env'

export interface AppProperties {
  /**
   * appllication process absolute path
   */
  processPath?: string

  /**
   * current profile
   */
  profile?: Profile | string

  /**
   * config file dir path (relative)
   */
  configDir?: string

  /**
   * application device type
   */
  deviceType?: DeviceType

  /**
   * application version
   */
  version?: string

  /**
   * application title
   */
  title?: string

  /**
   * 인증 설정
   */
  auth?: {
    /**
     * 활성화 여부
     */
    enabled?: boolean

    /**
     * 인증 유형
     */
    type?: 'jwt' | 'server'

    /**
     * 로그인 URL
     */
    loginUrl?: string

    /**
     * 인증에 실패 처리 유형
     *  redirect : 로그인 페이지로 리다이렉트
     *  error : 에러 발생 (에러 페이지로 이동)
     *  event : 이벤트만 발생
     *
     *  @default redirect
     */
    failProcessType?: 'redirect' | 'error' | 'event'

    /**
     * 인증이 만료되었을 경우 처리 유형
     *  logout : 로그아웃 처리
     *  error : 에러 발생 (에러 페이지로 이동)
     *  event : 이벤트
     *
     *  @default logout
     */
    expiredProcessType?: 'logout' | 'error' | 'event'

    /**
     * 에러 처리를 할 경우, 에러 페이지 경로
     */
    errorUrl?: string

    /**
     * 에러 처리를 할 경우, 상태 코드
     * @default 401
     */
    errorStatusCode?: number

    /**
     * 자동 로그아웃(초)
     * @default 0
     */
    autoLogoutSeconds?: number

    /**
     * 인증 처리를 수행하지 않을 URL 적용
     * @see micromatch
     */
    excludeUrlPatterns?: string[]

    /**
     * 인증 체크 function
     */
    checkModulePath?: string

    /**
     * jwt 상세 설정
     */
    jwt?: {
      /**
       * 쿠키 사용 여부
       */
      useCookie?: boolean

      /**
       * 쿠키 경로
       * @default /
       */
      cookiePath?: string

      /**
       * access token key
       */
      accessTokenKey?: string

      /**
       * refresh token key
       */
      refreshTokenKey?: string

      /**
       * custom auth check module
       */
      customAuthCheckModule?: string

      /**
       * 중복 인증 체크 유형
       */
      duplicationCheckType?: 'websocket' | 'polling' | 'none'

      /**
       * 중복 인증 체크 유형이 websocket일 경우, 사용할 URL
       * @deprecated duplicationCheckUrl을 사용
       */
      webSocketUrl?: string

      /**
       * 중복 확인 URL
       */
      duplicationCheckUrl?: string

      /**
       * 폴링 요청 간격
       * @default 10000
       */
      pollingDurationMilliSec?: number
    }
  }
}

export interface ServerProperties {
  /**
   * 서버유형
   */
  type?: ServerType

  /**
   * 로컬 개발용 서버 여부
   * @readonly
   */
  readonly dev?: boolean

  /**
   * server port
   * @default process.env.PORT
   */
  port?: number

  /**
   * server host
   * @default process.env.HOST
   */
  host?: string

  /**
   * context path
   * @default null
   */
  contextPath?: string

  /**
   * https protocol 사용 여부
   * @default false
   */
  https?: {
    /**
     * https 활성화 여부
     */
    enabled: boolean

    /**
     * key file 경로
     */
    key?: string | Buffer

    /**
     * cert 파일 경로
     */
    cert?: string | Buffer
  }

  /**
   * 로컬 호출 base Url
   * @readonly
   */
  readonly localBaseUrl?: string

  /**
   * API 서버 base Url
   */
  apiServerBaseUrl?: string

  /**
   * 이미지 서버 url
   */
  imgServerUrl?: string

  /**
   * 미들웨어 설정 옵션
   */
  middleware?: ServerMiddlewareProperties


  /**
   * custom 추가 항목
   */
  [key: string]: any
}

export interface ServerMiddlewareProperties {
  /**
   * 서버 미들웨어 목록
   */
  middlewares?: ServerMiddleware[]

  /**
   * static locations
   */
  staticLocations?: StaticLocation[]

  /**
   * 서버 압축 사용여부 (gzip 압축)
   */
  compress?: boolean

  /**
   * bodyParser 사용여부 (json)
   */
  bodyParser?: boolean | OptionsJson

  /**
   * bodyParser url encoded
   */
  bodyParserUrlencoded?: boolean | OptionsUrlencoded

  /**
   * multipart 사용 여부
   */
  multipart?: boolean | MiddleWareOption
}

/**
 * 미들 웨어 옵션
 */
export interface MiddleWareOption {
  /**
   * 활성화 여부
   */
  enabled: boolean

  /**
   * 제외 URL 패턴
   */
  excludeUrlPatterns?: string[]
}

export interface LoggerProperties {
  /**
   * logging level
   * @defualt debug
   */
  level?: LogLevel

  /**
   * logger name
   * @defualt ustra
   */
  name?: string

  /**
   * 비동기 여부
   */
  async?: boolean

  /**
   * 파일 로깅 적재 여부
   * @defualt false
   */
  file?: boolean

  /**
   * 파일 로깅 시 적재 directory
   * @default applicationRoot/logs
   */
  dirname?: string

  /**
   * file logging name
   * %DATE% 문자열은 datePattern으로 replace
   * @defualt application.%DATE%.log
   */
  filename?: string

  /**
   * error 로그를 저장할 파일 명
   * 파일명을 설정할 경우, error level의 별도 로그를 적재한다.
   * @default error.%DATE%.log
   */
  errFilename?: string

  /**
   * 일자 패턴
   * @default YYYY-MM-DD
   * YYYY-MM-DD-HH로 설정할 경우, 시간별 rotate
   */
  datePattern?: string

  /**
   * 백업 파일 zip 압축 여부
   */
  zippedArchive?: boolean

  /**
   * file logging size (byte)
   * number : byte 길이
   * string : k, m, g 로 파일 사이즈 단위
   * @default null
   */
  maxSize?: number | string

  /**
   * 로그 파일 유지 수
   * number : 파일 수
   * string : number + 'd' => 일수
   */
  maxFiles?: number | string

  /**
   * browser console 비활성화 여부
   */
  disableBrowserConsole?: boolean
}

export default interface ApplicationProperties {
  /**
   * App 설정
   */
  app?: AppProperties

  /**
   * Logger 설정
   */
  logger?: LoggerProperties

  /**
   * 서버 설정 값
   * @type {ServerProperties}
   * @memberof HanatourProperties
   */
  server?: ServerProperties

  /**
   * custom 추가 항목
   */
  [key: string]: any
}

export const defaultApplicationProperties: ApplicationProperties = {
  app: {
    deviceType: DeviceType.ALL,
    auth: {
      enabled: false,
      loginUrl: '/',
      failProcessType: 'redirect',
      expiredProcessType: 'logout',
      errorStatusCode: 401,
      autoLogoutSeconds: 0,
      excludeUrlPatterns: [],
      type: 'jwt',
      jwt: {
        useCookie: true,
        cookiePath: '/',
        duplicationCheckType: 'none',
        pollingDurationMilliSec: 10000
      },
    },
  },
  logger: {
    name: 'ustra',
    level: LogLevel.Debug,
    file: false,
    maxSize: null,
    zippedArchive: false,
    filename: 'application.%DATE%.log',
    errFilename: 'error.%DATE%.log',
    disableBrowserConsole: false,
  },
  server: {
    type: ServerType.EXPRESS,
    host: '0.0.0.0',
    https: {
      enabled: false,
    },
    dev: false,
    contextPath: null,
    middleware: {
      compress: true,
      bodyParser: true,
      bodyParserUrlencoded: { extended: true }
    },
  },
}
